/**
 * Centralized route configuration
 * Use this file to define all application routes
 * This ensures type safety and makes route changes easier to manage
 */

export const ROUTES = {
  // Root
  HOME: '/',

  // Authentication
  AUTH: '/auth',

  // Access links
  ACCESS: '/access/:code',

  // Masterclass home (list of masterclasses)
  MASTERCLASS_HOME: '/masterclasses',

  // Cases for a specific masterclass (parameterized)
  MASTERCLASS_CASES: '/masterclass/:masterclassId/cases',

  // Case detail within a masterclass - URL: /masterclass/:masterclassId/:caseId
  MASTERCLASS_CASE_DETAIL: '/masterclass/:masterclassId/:caseId',

  // Stats for a masterclass (aggregated across all cases)
  MASTERCLASS_STATS_OVERVIEW: '/masterclass/:masterclassId/stats',

  // Stats for a case within a masterclass
  MASTERCLASS_STATS: '/masterclass/:masterclassId/:caseId/stats',

  // Fallback
  NOT_FOUND: '*',
} as const satisfies Record<string, string>;

/**
 * Helper function to generate masterclass cases route
 * @param masterclassId - The masterclass ID to navigate to
 * @returns The formatted route path
 */
export const getMasterclassCasesRoute = (masterclassId: string): string => {
  return `/masterclass/${masterclassId}/cases`;
};

/**
 * Helper function to generate case detail route within a masterclass
 * URL: /masterclass/:masterclassId/:caseId
 * @param masterclassId - The masterclass ID
 * @param caseId - The case ID (legacyCaseId or GraphQL id)
 * @returns The formatted route path
 */
export const getMasterclassCaseRoute = (masterclassId: string, caseId: string): string => {
  return `/masterclass/${masterclassId}/${caseId}`;
};

/**
 * Helper function to generate masterclass-level stats route
 * @param masterclassId - The masterclass ID
 * @returns The formatted route path
 */
export const getMasterclassStatsOverviewRoute = (masterclassId: string): string => {
  return `/masterclass/${masterclassId}/stats`;
};

/**
 * Helper function to generate stats route within a masterclass
 * @param masterclassId - The masterclass ID
 * @param caseId - The case ID
 * @returns The formatted route path
 */
export const getMasterclassStatsRoute = (masterclassId: string, caseId: string): string => {
  return `/masterclass/${masterclassId}/${caseId}/stats`;
};

/**
 * Type for all available routes
 */
export type AppRoute = (typeof ROUTES)[keyof typeof ROUTES];
